/* eslint-disable */
import { useState, useEffect, useMemo } from "react";
import {
  Typography,
  Button,
  Box,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormLabel,
  Chip,
} from "@mui/material";
import { fetcher, postFetcher } from "src/utils/axios";
import { enqueueSnackbar } from "notistack";

const AddUnitDialog = ({ open, onClose, onSave, existingUnits }) => {
  const [unitName, setUnitName] = useState("");
  const [unitAbbreviation, setUnitAbbreviation] = useState("");

  const handleSave = () => {
    console.log('AddUnitDialog handleSave called, unitAbbreviation:', unitAbbreviation);
    if (existingUnits.includes(unitAbbreviation.trim())) {
      alert("This unit already exists");
      return;
    }
    onSave(unitAbbreviation.trim());
    setUnitName("");
    setUnitAbbreviation("");
    onClose();
  };

  const handleClose = () => {
    console.log('AddUnitDialog handleClose called');
    setUnitName("");
    setUnitAbbreviation("");
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add New Unit</DialogTitle>
      <DialogContent>
        <TextField
          label="Unit Abbreviation"
          value={unitAbbreviation}
          onChange={(e) => setUnitAbbreviation(e.target.value)}
          fullWidth
          margin="normal"
          placeholder="e.g., kg"
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} variant="outlined">
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          color="primary"
          disabled={!unitAbbreviation.trim()}
        >
          Add Unit
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const AddNewInputFormulaDialog = ({ open, onClose, onSave, rawMaterialInputs, previousNodeInputs, showDialog }) => {
  console.log('AddinputDialog rendering, props:', { open, rawMaterialInputs, previousNodeInputs, showDialog });
  const [numberOfOutputs, setNumberOfOutputs] = useState(1);
  const [outputs, setOutputs] = useState([
    { name: "", outputName: "", unit: "", description: "", quantity: 0, isConnected: "no", connectedOutput: "", inputForNextProcess: "no" },
  ]);
  const [availableUnits, setAvailableUnits] = useState([]);
  const [showAddUnitDialog, setShowAddUnitDialog] = useState(false);
  const [attributesList, setAttributesList] = useState([]);
  const [nodeOutputs, setNodeOutputs] = useState([]);
  const [selectedNodeOutput, setSelectedNodeOutput] = useState({});
  const [allOutputData, setAllOutputData] = useState([]);
  const [editingIndex, setEditingIndex] = useState(null);
  const [linked_attributes, setLinkedAttributes] = useState([]);

  const stableRawMaterialInputs = useMemo(() => rawMaterialInputs || [], [rawMaterialInputs]);
  const stableNodeInputs = useMemo(() => previousNodeInputs || [], [previousNodeInputs]);

  useEffect(() => {
    console.log("Updated nodeOutputs", nodeOutputs);
  }, [nodeOutputs]);

  useEffect(() => {
    console.log("Updated linked_attributes:", linked_attributes);
  }, [linked_attributes]);

  useEffect(() => {
    console.log('showDialog', showDialog);
    if (!showDialog) {
      console.log('Resetting state because showDialog is false');
      setNumberOfOutputs(1);
      setOutputs([
        { name: "", outputName: "", unit: "", description: "", quantity: 0, isConnected: "no", connectedOutput: "", inputForNextProcess: "no" },
      ]);
      setAllOutputData([]);
      setSelectedNodeOutput({});
      setNodeOutputs([]);
      setAvailableUnits([]);
      setAttributesList([]);
      setEditingIndex(null);
      setLinkedAttributes([]);
    }
  }, [showDialog]);

  useEffect(() => {
    if (numberOfOutputs > stableRawMaterialInputs.length) {
      setNumberOfOutputs(stableRawMaterialInputs.length || 1);
    }
  }, [numberOfOutputs, stableRawMaterialInputs]);

  const getAllLinkedAttributes = useMemo(() => {
    const allAttributes = [];
    
    // Add raw material attributes
    attributesList.forEach(material => {
      material.attributes.forEach(attribute => {
        allAttributes.push({
          productId: material.id,
          attribute: attribute,
          displayName: `${material.name}: ${attribute}`,
          type: 'material'
        });
      });
    });
    
    // Add node output linked attributes
    nodeOutputs.forEach(node => {
      node.outputs.forEach(output => {
        if (output.linked_attributes && output.linked_attributes.length > 0) {
          output.linked_attributes.forEach(attr => {
            const product = attributesList.find(m => m.id === attr.productId);
            const productName = product ? product.name : `Product ${attr.productId}`;
            allAttributes.push({
              productId: attr.productId,
              attribute: attr.attribute,
              displayName: `${output.name} (${node.nodeName}): ${productName}.${attr.attribute}`,
              type: 'node_output',
              nodeName: node.nodeName,
              outputName: output.name
            });
          });
        }
      });
    });
    
    console.log('All available linked attributes:', allAttributes);
    return allAttributes;
  }, [attributesList, nodeOutputs]);

  const fetchNodeAttributes = async () => {
    try {
      console.log('fetchNodeAttributes called with stableNodeInputs:', stableNodeInputs);
      if (!stableNodeInputs || stableNodeInputs.length === 0) {
        console.log("No previous node inputs provided");
        setNodeOutputs([]);
        return;
      }
      const nodeOutputsData = [];
      for (const node of stableNodeInputs) {
        if (node.backendId) {
          const res = await fetcher(`/new/workflow-app/node/${node.backendId}/`);
          console.log(`Fetch Node Attributes Response for backendId ${node.backendId}:`, res);
          if (res && res.output_foreign) {
            const outputs = res.output_foreign.map(output => ({
              id: output.id,
              name: output.name,
              attributes: output.attributes.map(attr => attr.key),
              linked_attributes: output.linked_attributes || [],
            }));
            nodeOutputsData.push({
              nodeId: node.backendId,
              nodeName: node.label,
              outputs,
            });
          }
        }
      }
      console.log("Processed Node Outputs:", nodeOutputsData);
      setNodeOutputs(nodeOutputsData);
    } catch (error) {
      console.error("Error fetching node attributes:", error);
      setNodeOutputs([]);
      enqueueSnackbar("Failed to fetch node outputs", { variant: "error" });
    }
  };

  const fetchUnits = async () => {
    try {
      console.log('fetchUnits called with stableRawMaterialInputs:', stableRawMaterialInputs);
      if (!stableRawMaterialInputs || stableRawMaterialInputs.length === 0) {
        console.log("No raw material inputs provided");
        setAvailableUnits([]);
        return;
      }
      const rawMaterialIds = stableRawMaterialInputs.map((material) => Number(material.value));
      console.log('rawMaterialIds:', rawMaterialIds);
      const payload = { product_ids: rawMaterialIds };
      console.log("Fetching units with payload:", payload);
      const res = await postFetcher(["/new/workflow-app/products/unit/", payload]);
      console.log("Fetch Units Response:", res);
      if (res) {
        const units = Array.isArray(res)
          ? [...new Set(res.flatMap((item) => item.units || []))]
          : res.data?.units || [];
        console.log("Processed Units:", units);
        setAvailableUnits(units.length ? units : []);
      } else {
        console.log("No data in response");
        setAvailableUnits([]);
      }
    } catch (error) {
      console.error("Error fetching units:", error);
      setAvailableUnits([]);
      enqueueSnackbar("Failed to fetch units", { variant: "error" });
    }
  };

  const fetchAttributes = async () => {
    try {
      console.log('fetchAttributes called with stableRawMaterialInputs:', stableRawMaterialInputs);
      if (!stableRawMaterialInputs || stableRawMaterialInputs.length === 0) {
        console.log("No raw material inputs provided");
        setAttributesList([]);
        return;
      }
      const rawMaterialIds = stableRawMaterialInputs.map((material) => Number(material.value));
      console.log('rawMaterialIds:', rawMaterialIds);
      const payload = { product_ids: rawMaterialIds };
      console.log("Fetching attributes with payload:", payload);
      const res = await postFetcher(["/new/workflow-app/products/attributes/key/", payload]);
      console.log("Fetch Attributes Response:", res);
      if (res) {
        const attributes = Array.isArray(res)
          ? res.map(item => ({
            name: item.name,
            attributes: item.attributes || [],
            id: item.id,
          }))
          : res.data?.map(item => ({
            name: item.name,
            attributes: item.attributes || [],
            id: item.id,
          })) || [];
        console.log("Processed Attributes:", attributes);
        setAttributesList(attributes.length ? attributes : []);
      } else {
        console.log("No data in response");
        setAttributesList([]);
      }
    } catch (error) {
      console.error("Error fetching attributes:", error);
      setAttributesList([]);
      enqueueSnackbar("Failed to fetch attributes", { variant: "error" });
    }
  };

  useEffect(() => {
    if (showDialog) {
      console.log('useEffect for fetch called');
      fetchUnits();
      fetchAttributes();
      fetchNodeAttributes();
    }
  }, [stableRawMaterialInputs, stableNodeInputs, showDialog]);

  const handleNumberOfOutputsChange = (value) => {
    console.log('handleNumberOfOutputsChange called, value:', value);
    try {
      let num = parseInt(value) || 1;
      num = Math.max(1, Math.min(10, num));
      if (num > stableRawMaterialInputs.length) {
        enqueueSnackbar(`Number of input formulas cannot exceed number of raw materials (${stableRawMaterialInputs.length})`, { variant: "error" });
        return;
      }
      setNumberOfOutputs(num);
      const newOutputs = [...outputs];
      if (num > newOutputs.length) {
        for (let i = newOutputs.length; i < num; i++) {
          newOutputs.push({
            name: "",
            outputName: "",
            unit: "",
            description: "",
            quantity: 0,
            isConnected: "no",
            connectedOutput: "",
            inputForNextProcess: "no",
          });
        }
      } else {
        newOutputs.splice(num);
      }
      console.log('New outputs state:', newOutputs);
      setOutputs(newOutputs);
      setSelectedNodeOutput((prev) => {
        const newSelections = {};
        for (let i = 0; i < num; i++) {
          newSelections[i] = prev[i] || "";
        }
        return newSelections;
      });
      setLinkedAttributes((prev) => {
        const newLinkedAttributes = prev.filter((_, i) => i < num);
        for (let i = newLinkedAttributes.length; i < num; i++) {
          newLinkedAttributes.push([]);
        }
        return newLinkedAttributes;
      });
    } catch (error) {
      console.error('Error in handleNumberOfOutputsChange:', error);
      enqueueSnackbar("Error updating number of outputs", { variant: "error" });
    }
  };

  const handleOutputChange = (index, field, value) => {
    console.log('handleOutputChange called, index:', index, 'field:', field, 'value:', value);
    try {
      const newOutputs = [...outputs];
      if (field === "quantity") {
        const parsedValue = parseFloat(value);
        newOutputs[index][field] = Number.isNaN(parsedValue) || parsedValue < 0 ? 0 : parsedValue;
      } else {
        newOutputs[index][field] = value;
      }
      if (field === "isConnected" && value === "no") {
        newOutputs[index].connectedOutput = "";
      }
      console.log('Updated outputs state:', newOutputs);
      setOutputs(newOutputs);
    } catch (error) {
      console.error('Error in handleOutputChange:', error);
      enqueueSnackbar("Error updating output", { variant: "error" });
    }
  };

  const handleNodeOutputChange = (index, value) => {
    console.log('handleNodeOutputChange called, index:', index, 'value:', value);
    setSelectedNodeOutput((prev) => ({
      ...prev,
      [index]: value,
    }));
  };

  const handleAttributeClick = (outputIndex, materialName, attribute, type) => {
    console.log('=== handleAttributeClick START ===');
    console.log('outputIndex:', outputIndex, 'materialName:', materialName, 'attribute:', attribute, 'type:', type);
    try {
      const newOutputs = [...outputs];
      const currentFormula = newOutputs[outputIndex].name.trim();
      
      let attributeRef = "";
      
      if (type === 'node_output') {
        const selectedOutputName = selectedNodeOutput[outputIndex];
        const selectedOutput = nodeOutputs.flatMap(node => node.outputs).find(o => o.name === selectedOutputName);
        
        console.log('Selected output:', selectedOutput);
        
        if (selectedOutput && selectedOutput.linked_attributes) {
          const linkedAttr = selectedOutput.linked_attributes.find(attr => 
            attr.attribute === attribute
          );
          console.log('Found linked attribute:', linkedAttr);
          
          if (linkedAttr) {
            attributeRef = `${linkedAttr.productId}.${attribute}`;
          } else {
            const material = attributesList.find(m => m.name === materialName);
            attributeRef = material ? `${material.id}.${attribute}` : `${materialName}.${attribute}`;
          }
        }
      } else if (type === 'material') {
        const material = attributesList.find(m => m.name === materialName);
        console.log('Found material:', material);
        attributeRef = material ? `${material.id}.${attribute}` : `${materialName}.${attribute}`;
      } else if (type === 'output') {
        attributeRef = `${materialName}.${attribute}`;
      }
      
      console.log('Generated attributeRef:', attributeRef);
      
      const newFormula = currentFormula ? `${currentFormula} ${attributeRef}` : attributeRef;
      newOutputs[outputIndex].name = newFormula;
      console.log('Updated formula:', newFormula);
      setOutputs(newOutputs);
      console.log('=== handleAttributeClick END ===');
    } catch (error) {
      console.error('Error in handleAttributeClick:', error);
      enqueueSnackbar("Error adding attribute to formula", { variant: "error" });
    }
  };

  const handleOperatorClick = (outputIndex, operator) => {
    console.log('handleOperatorClick called, outputIndex:', outputIndex, 'operator:', operator);
    try {
      const newOutputs = [...outputs];
      const currentFormula = newOutputs[outputIndex].name.trim();
      const newFormula = currentFormula ? `${currentFormula} ${operator} ` : `${operator} `;
      newOutputs[outputIndex].name = newFormula;
      console.log('Updated formula:', newFormula);
      setOutputs(newOutputs);
    } catch (error) {
      console.error('Error in handleOperatorClick:', error);
      enqueueSnackbar("Error adding operator to formula", { variant: "error" });
    }
  };

  const handleLinkedAttributesChange = (index, value) => {
    console.log('handleLinkedAttributesChange called, index:', index, 'value:', value);
    try {
      const newLinkedAttributes = [...linked_attributes];
      const selectedAttributes = value.map((val) => {
        const [productId, attribute] = val.split(':');
        return { productId: Number(productId), attribute };
      });
      newLinkedAttributes[index] = selectedAttributes;
      console.log('Updated linked_attributes:', newLinkedAttributes);
      setLinkedAttributes(newLinkedAttributes);
    } catch (error) {
      console.error('Error in handleLinkedAttributesChange:', error);
      enqueueSnackbar("Error updating linked attributes", { variant: "error" });
    }
  };

  const reconstructFormulaString = (formula, attributesList) => {
    if (!formula || !formula.operation || !formula.operands) {
      return "";
    }

    const operatorMap = {
      add: "+",
      subtract: "-",
      multiply: "*",
      divide: "/",
    };

    const mapIdToName = (operand) => {
      if (typeof operand !== "string") {
        const nestedFormula = reconstructFormulaString(operand, attributesList);
        return nestedFormula ? `(${nestedFormula})` : "";
      }
      if (!operand.includes(".")) {
        return operand;
      }
      const [id, attribute] = operand.split(".");
      const material = attributesList.find((m) => m.id.toString() === id);
      return material ? `${material.name}.${attribute}` : operand;
    };

    const operator = operatorMap[formula.operation] || "";
    const mappedOperands = formula.operands.map(mapIdToName);
    return mappedOperands.join(` ${operator} `);
  };

  const parseFormulaToPayload = (formulaStr, isConnected) => {
    console.log('=== parseFormulaToPayload START ===');
    console.log('Input formulaStr:', formulaStr);
    console.log('isConnected:', isConnected);
    
    try {
      formulaStr = formulaStr.replace(/\s+/g, ' ').trim();
      const operatorMap = { '+': 'add', '-': 'subtract', '*': 'multiply', '/': 'divide' };
      const precedence = { '+': 1, '-': 1, '*': 2, '/': 2 };
      
      console.log('Cleaned formulaStr:', formulaStr);
      
      const tokens = formulaStr.match(/(\d+\.\w+|\d+(?:\.\d+)?|[a-zA-Z_][a-zA-Z0-9_]*(?:\.[a-zA-Z_][a-zA-Z0-9_]*)+|[+\-*\/()])/g) || [];
      console.log('Extracted tokens:', tokens);
      
      if (tokens.length === 0) {
        console.log('No tokens found, returning empty result');
        return { operation: "", operands: [], formulaStr, product: [], nodes: [] };
      }

      const getReferenceId = (materialName) => {
        console.log('getReferenceId called with materialName:', materialName);
        materialName = materialName.trim();
        const material = attributesList.find((m) => m.name === materialName);
        if (material) {
          console.log('Found material:', material);
          return { id: material.id, type: 'material' };
        }
        const output = allOutputData.find((o) => o.name === materialName);
        if (output) {
          console.log('Found output:', output);
          return { id: output.name, type: 'output' };
        }
        const nodeOutput = nodeOutputs.flatMap(node => node.outputs).find(o => o.name === materialName);
        if (nodeOutput) {
          console.log('Found nodeOutput:', nodeOutput);
          return { id: nodeOutput.id, type: 'node_output' };
        }
        console.log('No reference found for:', materialName);
        return { id: materialName, type: 'unknown' };
      };

      const usedProductIds = new Set();
      const usedNodeIds = new Set();

      const parseExpression = (tokens, minPrecedence = 0) => {
        console.log('parseExpression called with tokens:', tokens, 'minPrecedence:', minPrecedence);
        let left = parseAtom(tokens);
        console.log('Left after parseAtom:', left);
        
        while (tokens.length > 0 && tokens[0] in operatorMap && precedence[tokens[0]] >= minPrecedence) {
          const op = tokens.shift();
          console.log('Processing operator:', op);
          const rightPrecedence = precedence[op] + 1;
          const right = parseExpression(tokens, rightPrecedence);
          console.log('Right operand:', right);
          left = { operation: operatorMap[op], operands: [left, right] };
          console.log('New left after operation:', left);
        }
        return left;
      };

      const parseAtom = (tokens) => {
        console.log('parseAtom called with tokens:', tokens);
        if (!tokens.length) throw new Error('Unexpected end of formula');
        
        if (tokens[0] === '(') {
          console.log('Found opening parenthesis');
          tokens.shift();
          const expr = parseExpression(tokens);
          if (!tokens.length || tokens[0] !== ')') throw new Error('Unmatched parenthesis');
          tokens.shift();
          console.log('Expression inside parentheses:', expr);
          return expr;
        }
        
        const token = tokens.shift();
        console.log('Processing token:', token);
        
        if (token.match(/^\d+\.\w+$/) || token.match(/^[a-zA-Z_][a-zA-Z0-9_]*\.[a-zA-Z_][a-zA-Z0-9_]*$/)) {
          console.log('Token is an attribute reference:', token);
          
          const lastDotIndex = token.lastIndexOf('.');
          const materialPart = token.substring(0, lastDotIndex);
          const attribute = token.substring(lastDotIndex + 1).trim();
          
          console.log('materialPart:', materialPart, 'attribute:', attribute);
          
          const materialId = parseFloat(materialPart);
          if (!Number.isNaN(materialId)) {
            console.log('Material part is numeric ID:', materialId);
            usedProductIds.add(materialId);
            console.log('Added to usedProductIds:', materialId);
            return token;
          }
          
          const reference = getReferenceId(materialPart);
          console.log('Reference result:', reference);
          
          if (reference.type === 'material') {
            usedProductIds.add(reference.id);
            console.log('Added to usedProductIds:', reference.id);
          } else if (reference.type === 'node_output') {
            const node = nodeOutputs.find(n => n.outputs.some(o => o.id === reference.id));
            if (node) {
              usedNodeIds.add(node.nodeId);
              console.log('Added to usedNodeIds:', node.nodeId);
            }
          }
          
          const attributeRef = `${reference.id}.${attribute}`;
          console.log('Returning attribute reference:', attributeRef);
          return attributeRef;
        }
        
        const num = parseFloat(token);
        if (!Number.isNaN(num)) {
          console.log('Token is a number:', num);
          return num;
        }
        
        console.log('Unknown token structure:', token);
        throw new Error(`Unknown token: ${token}`);
      };

      console.log('Starting expression parsing with tokens:', [...tokens]);
      const parsed = parseExpression([...tokens]);
      console.log('Final parsed result:', JSON.stringify(parsed, null, 2));
      
      const product = Array.from(usedProductIds);
      const nodes = Array.from(usedNodeIds);
      console.log('Products used in formula:', product);
      console.log('Nodes used in formula:', nodes);
      
      console.log('=== parseFormulaToPayload END ===');
      return { ...parsed, formulaStr, product, nodes };
    } catch (error) {
      console.error('Error parsing formula:', error);
      enqueueSnackbar('Invalid formula. Please check syntax.', { variant: 'error' });
      return { operation: "", operands: [], formulaStr, product: [], nodes: [] };
    }
  };

  const handleBackspace = (index, event) => {
    console.log('handleBackspace called, index:', index, 'event.key:', event.key);
    try {
      if (event.key !== "Backspace") return;
      const input = event.target;
      const cursorAtEnd = input.selectionStart === input.value.length && input.selectionEnd === input.value.length;
      if (cursorAtEnd) {
        const newOutputs = [...outputs];
        const currentFormula = newOutputs[index].name.trim();
        if (!currentFormula) return;
        const tokens = currentFormula.split(/\s+/).filter(Boolean);
        tokens.pop();
        const newFormula = tokens.join(' ');
        newOutputs[index].name = newFormula;
        console.log('Updated formula after backspace:', newFormula);
        setOutputs(newOutputs);
        event.preventDefault();
      }
    } catch (error) {
      console.error('Error in handleBackspace:', error);
      enqueueSnackbar("Error handling backspace", { variant: "error" });
    }
  };

  const handleAddUnitFromDialog = (newUnit) => {
    console.log("Adding new unit:", newUnit);
    try {
      setAvailableUnits((prevUnits) => {
        const updatedUnits = [...prevUnits, newUnit];
        console.log("Updated availableUnits:", updatedUnits);
        return updatedUnits;
      });
      setShowAddUnitDialog(false);
    } catch (error) {
      console.error('Error in handleAddUnitFromDialog:', error);
      enqueueSnackbar("Error adding new unit", { variant: "error" });
    }
  };

  const handleEditOutput = (index) => {
    console.log('handleEditOutput called, index:', index);
    try {
      const outputToEdit = allOutputData[index];
      if (!outputToEdit) {
        enqueueSnackbar(`No saved output found at index ${index + 1}`, { variant: 'error' });
        return;
      }
      const formulaString = reconstructFormulaString(outputToEdit.formula, attributesList);
      const newOutputs = [...outputs];
      newOutputs[index] = {
        name: formulaString,
        outputName: outputToEdit.name || "",
        unit: outputToEdit.value?.unit || "",
        description: outputToEdit.description || "",
        quantity: outputToEdit.value?.quantity || 0,
        isConnected: outputToEdit.parent_output ? "yes" : "no",
        connectedOutput: outputToEdit.parent_output || "",
        inputForNextProcess: outputToEdit.input_for_next_process ? "yes" : "no",
      };
      setOutputs(newOutputs);
      setLinkedAttributes((prev) => {
        const newLinkedAttributes = [...prev];
        newLinkedAttributes[index] = outputToEdit.linked_attributes || [];
        return newLinkedAttributes;
      });
      setEditingIndex(index);
      enqueueSnackbar(`Editing Input Formula ${index + 1}`, { variant: 'info' });
    } catch (error) {
      console.error('Error in handleEditOutput:', error);
      enqueueSnackbar(`Failed to edit Input Formula ${index + 1}`, { variant: 'error' });
    }
  };

  const handleSaveOutput = (index) => {
    console.log('handleSaveOutput called, index:', index, 'output:', outputs[index]);
    try {
      const output = outputs[index];
      if (output.isConnected === "yes" && !output.connectedOutput) {
        enqueueSnackbar(`Please select a connected output for "${output.outputName}" or change connection status to "No"`, { variant: 'error' });
        return;
      }
      const formula = parseFormulaToPayload(output.name, output.isConnected);
      const outputData = {
        name: output.outputName,
        value: {
          quantity: output.quantity,
          unit: output.unit,
        },
        linked_attributes: linked_attributes[index] || [],
        description: output.description || "",
        input_for_next_process: output.inputForNextProcess === "yes",
        parent_output: output.isConnected === "yes"
          ? allOutputData.find((o) => o.name === output.connectedOutput)?.name || null
          : null,
        ...(formula?.operation && formula.operands?.length > 0 && {
          formula: {
            operation: formula.operation,
            operands: formula.operands,
          },
        }),
        ...(formula?.product && { product: formula.product }),
        ...(formula?.nodes && { nodes: formula.nodes }),
      };
      console.log('Saving output data:', outputData);

      setAllOutputData((prev) => {
        const newData = [...prev];
        if (editingIndex === index) {
          newData[index] = outputData;
        } else {
          newData.push(outputData);
        }
        return newData;
      });

      if (selectedNodeOutput[index]) {
        const selectedOutput = nodeOutputs
          .flatMap(node => node.outputs)
          .find(o => o.name === selectedNodeOutput[index])
          ?.id;
        if (selectedOutput) {
          onSave({
            outputId: selectedOutput,
            outputName: output.outputName,
            label: output.outputName
          });
        }
      }

      if (index === numberOfOutputs - 1 && editingIndex === null) {
        console.log('Saving all outputs to onSave');
        onSave(allOutputData.concat([outputData]));
        onClose();
      }

      setEditingIndex(null);
      enqueueSnackbar(`Input Formula ${index + 1} ${editingIndex === index ? 'updated' : 'saved'} successfully`, { variant: 'success' });

      const newOutputs = [...outputs];
      newOutputs[index] = {
        name: "",
        outputName: "",
        unit: "",
        description: "",
        quantity: 0,
        isConnected: "no",
        connectedOutput: "",
        inputForNextProcess: "no",
      };
      setOutputs(newOutputs);
      setLinkedAttributes((prev) => {
        const newLinkedAttributes = [...prev];
        newLinkedAttributes[index] = [];
        return newLinkedAttributes;
      });
    } catch (error) {
      console.error('Error in handleSaveOutput:', error);
      enqueueSnackbar(`Failed to save Input Formula ${index + 1}. Please check the console for details.`, { variant: 'error' });
    }
  };

  const handleSaveAll = () => {
    console.log('handleSaveAll called, outputs:', outputs);
    try {
      let hasError = false;
      const newOutputData = [...allOutputData];

      outputs.forEach((output, index) => {
        if (!output.outputName.trim()) {
          enqueueSnackbar(`Input Formula ${index + 1} is missing a name`, { variant: 'error' });
          hasError = true;
        }
        if (output.isConnected === "yes" && !output.connectedOutput) {
          enqueueSnackbar(`Input Formula ${index + 1} requires a connected output or change connection status to "No"`, { variant: 'error' });
          hasError = true;
        }
      });

      if (hasError) {
        console.log('Validation failed, aborting save');
        return;
      }

      outputs.forEach((output, index) => {
        const formula = parseFormulaToPayload(output.name, output.isConnected);
        const outputData = {
          name: output.outputName,
          value: {
            quantity: output.quantity,
            unit: output.unit,
          },
          linked_attributes: linked_attributes[index] || [],
          description: output.description || "",
          input_for_next_process: output.inputForNextProcess === "yes",
          parent_output: output.isConnected === "yes"
            ? allOutputData.find((o) => o.name === output.connectedOutput)?.name || null
            : null,
          ...(formula?.operation && formula.operands?.length > 0 && {
            formula: {
              operation: formula.operation,
              operands: formula.operands,
            },
          }),
          ...(formula?.product && { product: formula.product }),
          ...(formula?.nodes && { nodes: formula.nodes }),
        };

        if (editingIndex === index) {
          newOutputData[index] = outputData;
        } else {
          newOutputData[index] = outputData;
        }

        if (selectedNodeOutput[index]) {
          const selectedOutput = nodeOutputs
            .flatMap(node => node.outputs)
            .find(o => o.name === selectedNodeOutput[index])
            ?.id;
          if (selectedOutput) {
            onSave({
              outputId: selectedOutput,
              outputName: output.outputName,
              label: output.outputName,
            });
          }
        }
      });

      setAllOutputData(newOutputData);
      console.log('Saving all outputs to onSave:', newOutputData);
      onSave(newOutputData);
      setEditingIndex(null);
      enqueueSnackbar('All Input Formulas saved successfully', { variant: 'success' });

      const newOutputs = outputs.map(() => ({
        name: "",
        outputName: "",
        unit: "",
        description: "",
        quantity: 0,
        isConnected: "no",
        connectedOutput: "",
        inputForNextProcess: "no",
      }));
      setOutputs(newOutputs);
      setLinkedAttributes(newOutputs.map(() => []));
      onClose();
    } catch (error) {
      console.error('Error in handleSaveAll:', error);
      enqueueSnackbar('Failed to save all Input Formulas. Please check the console.', { variant: 'error' });
    }
  };

  const handleClose = () => {
    console.log('handleClose called');
    try {
      setNumberOfOutputs(1);
      setOutputs([
        { name: "", outputName: "", unit: "", description: "", quantity: 0, isConnected: "no", connectedOutput: "", inputForNextProcess: "no" },
      ]);
      setAllOutputData([]);
      setSelectedNodeOutput({});
      setNodeOutputs([]);
      setAvailableUnits([]);
      setAttributesList([]);
      setEditingIndex(null);
      setLinkedAttributes([]);
      onClose();
    } catch (error) {
      console.error('Error in handleClose:', error);
      enqueueSnackbar("Error closing dialog", { variant: "error" });
    }
  };

  const renderOutputs = () => {
    console.log('renderOutputs called, outputs:', outputs);
    try {
      return outputs.map((output, index) => {
        const isDisabled = index > allOutputData.length && editingIndex !== index;
        const selectedOutputName = selectedNodeOutput[index] || "";
        const selectedOutput = nodeOutputs.flatMap(node => node.outputs).find(o => o.name === selectedOutputName);
        const isSaved = index < allOutputData.length;
        const availableConnectedOutputs = allOutputData.filter((_, i) => i !== index);
        return (
          <Box
            key={index}
            sx={{
              mb: 3,
              p: 2,
              border: "1px solid #e0e0e0",
              borderRadius: 1,
              opacity: isDisabled ? 0.5 : 1,
              pointerEvents: isDisabled ? "none" : "auto",
            }}
          >
            <Typography variant="subtitle2" sx={{ mb: 2 }}>
              Input Formula {index + 1}
            </Typography>
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                Available Attributes by Material:
              </Typography>
              {attributesList.length > 0 ? (
                attributesList.map((material) => (
                  <Box key={material.name} sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                      {material.name} (Material)
                    </Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                      {material.attributes.map((attribute) => (
                        <Chip
                          key={`${material.name}-${attribute}`}
                          label={attribute}
                          onClick={() => handleAttributeClick(index, material.name, attribute, 'material')}
                          color="default"
                          variant="filled"
                          size="small"
                          sx={{ cursor: "pointer" }}
                        />
                      ))}
                    </Box>
                  </Box>
                ))
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No material attributes available
                </Typography>
              )}
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                Outputs from Previous Nodes:
              </Typography>
              {nodeOutputs.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  No outputs from previous nodes
                </Typography>
              ) : (
                <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                  <InputLabel>Select Node Output</InputLabel>
                  <Select
                    value={selectedOutputName}
                    onChange={(e) => handleNodeOutputChange(index, e.target.value)}
                    label="Select Node Output"
                    disabled={isDisabled || nodeOutputs.length === 0}
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    {nodeOutputs.flatMap(node =>
                      node.outputs.map(output => (
                        <MenuItem key={`${node.nodeId}-${output.id}`} value={output.name}>
                          {`${node.nodeName}: ${output.name}`}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>
              )}
              {selectedOutput && selectedOutput.linked_attributes && selectedOutput.linked_attributes.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                    Attributes for {selectedOutputName}
                  </Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                    {selectedOutput.linked_attributes.map((attr) => {
                      const product = attributesList.find(m => m.id === attr.productId);
                      const displayName = product ? product.name : `Product ${attr.productId}`;
                      return (
                        <Chip
                          key={`${selectedOutputName}-${attr.productId}-${attr.attribute}`}
                          label={`${displayName}.${attr.attribute}`}
                          onClick={() => handleAttributeClick(index, displayName, attr.attribute, 'node_output')}
                          color="primary"
                          variant="outlined"
                          size="small"
                          sx={{ cursor: "pointer" }}
                        />
                      );
                    })}
                  </Box>
                </Box>
              )}
              {output.isConnected === "yes" && availableConnectedOutputs.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                    Attributes for Connected Output: {output.connectedOutput}
                  </Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                    {availableConnectedOutputs.find(o => o.name === output.connectedOutput)?.formula?.operands
                      ?.filter(op => typeof op === 'string' && op.includes('.'))
                      ?.map(op => op.split('.')[1])
                      ?.map((attribute) => (
                        <Chip
                          key={`${output.connectedOutput}-${attribute}`}
                          label={attribute}
                          onClick={() => handleAttributeClick(index, output.connectedOutput, attribute, 'output')}
                          color="secondary"
                          variant="outlined"
                          size="small"
                          sx={{ cursor: "pointer" }}
                        />
                      )) || (
                        <Typography variant="body2" color="text.secondary">
                          No attributes available for this connected output
                        </Typography>
                      )}
                  </Box>
                </Box>
              )}
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, mt: 2 }}>
                Mathematical Operators:
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
                {["+", "-", "*", "/", "(", ")", "="].map((operator) => (
                  <Chip
                    key={operator}
                    label={operator}
                    onClick={() => handleOperatorClick(index, operator)}
                    color="primary"
                    variant="filled"
                    size="small"
                    sx={{ cursor: "pointer" }}
                  />
                ))}
              </Box>
            </Box>
            <Box sx={{ mb: 2 }}>
              <TextField
                label="Input Formula Name"
                value={output.outputName}
                onChange={(e) => handleOutputChange(index, "outputName", e.target.value)}
                fullWidth
                size="small"
                sx={{ mb: 2 }}
                placeholder="e.g., Cut Sheet"
                disabled={isDisabled && editingIndex !== index}
              />
              <TextField
                label="Input Formula"
                value={output.name}
                onChange={(e) => handleOutputChange(index, "name", e.target.value)}
                onKeyDown={(e) => handleBackspace(index, e)}
                fullWidth
                size="small"
                sx={{ mb: 2 }}
                multiline
                rows={2}
                placeholder="Click on attributes and operators above to build your formula"
                disabled={isDisabled && editingIndex !== index}
              />
              <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                <InputLabel>Linked Attributes</InputLabel>
                <Select
                  multiple
                  value={linked_attributes[index]?.map(attr => `${attr.productId}:${attr.attribute}`) || []}
                  onChange={(e) => handleLinkedAttributesChange(index, e.target.value)}
                  label="Linked Attributes"
                  disabled={isDisabled || getAllLinkedAttributes.length === 0}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => {
                        const [productId, attribute] = value.split(':');
                        const attrInfo = getAllLinkedAttributes.find(attr => 
                          attr.productId === Number(productId) && attr.attribute === attribute
                        );
                        return (
                          <Chip
                            key={value}
                            label={attrInfo?.displayName || `${productId}.${attribute}`}
                            size="small"
                          />
                        );
                      })}
                    </Box>
                  )}
                >
                  <MenuItem disabled>--- Raw Materials ---</MenuItem>
                  {getAllLinkedAttributes
                    .filter(attr => attr.type === 'material')
                    .map((attr) => (
                      <MenuItem key={`${attr.productId}-${attr.attribute}`} value={`${attr.productId}:${attr.attribute}`}>
                        {attr.displayName}
                      </MenuItem>
                    ))
                  }
                  <MenuItem disabled>--- Node Outputs ---</MenuItem>
                  {getAllLinkedAttributes
                    .filter(attr => attr.type === 'node_output')
                    .map((attr) => (
                      <MenuItem key={`${attr.productId}-${attr.attribute}-${attr.outputName}`} value={`${attr.productId}:${attr.attribute}`}>
                        {attr.displayName}
                      </MenuItem>
                    ))
                  }
                  {getAllLinkedAttributes.length === 0 && (
                    <MenuItem value="" disabled>
                      No attributes available
                    </MenuItem>
                  )}
                </Select>
              </FormControl>
              <TextField
                label="Description"
                value={output.description}
                onChange={(e) => handleOutputChange(index, "description", e.target.value)}
                fullWidth
                size="small"
                sx={{ mb: 2 }}
                multiline
                rows={2}
                placeholder="e.g., Smaller sheets (0.5x0.5 meter) cut from large sheets"
                disabled={isDisabled && editingIndex !== index}
              />
              <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                <InputLabel>Unit</InputLabel>
                <Select
                  value={output.unit}
                  onChange={(e) => handleOutputChange(index, "unit", e.target.value)}
                  label="Unit"
                  disabled={isDisabled && editingIndex !== index}
                >
                  {availableUnits.length > 0 ? (
                    availableUnits.map((unit) => (
                      <MenuItem key={unit} value={unit}>
                        {unit}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem value="" disabled>
                      No units available
                    </MenuItem>
                  )}
                </Select>
              </FormControl>
            </Box>
            <FormControl component="fieldset" sx={{ mb: 2 }}>
              <FormLabel component="legend">
                Is this input formula connected to any other input formula?
              </FormLabel>
              <RadioGroup
                row
                value={output.isConnected}
                onChange={(e) => handleOutputChange(index, "isConnected", e.target.value)}
                disabled={isDisabled && editingIndex !== index}
              >
                <FormControlLabel value="yes" control={<Radio />} label="Yes" />
                <FormControlLabel value="no" control={<Radio />} label="No" />
              </RadioGroup>
            </FormControl>
            {output.isConnected === "yes" && (
              <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                <InputLabel>Connected Input Formula</InputLabel>
                <Select
                  value={output.connectedOutput}
                  onChange={(e) => handleOutputChange(index, "connectedOutput", e.target.value)}
                  label="Connected Input Formula"
                  disabled={isDisabled && editingIndex !== index}
                >
                  {availableConnectedOutputs.length > 0 ? (
                    availableConnectedOutputs.map((output) => (
                      <MenuItem key={output.name} value={output.name}>
                        {output.name}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem value="" disabled>
                      No available input formulas
                    </MenuItem>
                  )}
                </Select>
              </FormControl>
            )}
            <FormControl component="fieldset" sx={{ mb: 2, ml: { xs: 0, md: 2 } }}>
              <FormLabel component="legend">
                Is this input formula an input for the next formula?
              </FormLabel>
              <RadioGroup
                row
                value={output.inputForNextProcess}
                onChange={(e) => handleOutputChange(index, "inputForNextProcess", e.target.value)}
                disabled={isDisabled && editingIndex !== index}
              >
                <FormControlLabel value="yes" control={<Radio />} label="Yes" />
                <FormControlLabel value="no" control={<Radio />} label="No" />
              </RadioGroup>
            </FormControl>
            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <Button
                onClick={() => handleSaveOutput(index)}
                variant="contained"
                color="primary"
                disabled={(isDisabled && editingIndex !== index) || !output.outputName.trim()}
                sx={{ mt: 2, mr: 1 }}
              >
                {editingIndex === index ? `Update Input Formula ${index + 1}` : `Save Input Formula ${index + 1}`}
              </Button>
              <Button
                onClick={() => handleEditOutput(index)}
                variant="contained"
                color="secondary"
                disabled={!isSaved}
                sx={{ mt: 2 }}
              >
                Edit Input Formula {index + 1}
              </Button>
            </Box>
          </Box>
        );
      });
    } catch (error) {
      console.error('Error in renderOutputs:', error);
      enqueueSnackbar("Error rendering outputs. Please check the console.", { variant: "error" });
      return <Typography color="error">Error rendering Input Formulas. Please check the console.</Typography>;
    }
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
        <DialogTitle>Add New Input Formula</DialogTitle>
        <DialogContent>
          <TextField
            label="Number of Input Formula Fields"
            type="number"
            value={numberOfOutputs}
            onChange={(e) => handleNumberOfOutputsChange(e.target.value)}
            inputProps={{ min: 1, max: Math.min(10, stableRawMaterialInputs.length || 10) }}
            margin="normal"
            sx={{ width: 200, mb: 2 }}
          />
          <Box sx={{ mb: 2 }}>
            <Button
              onClick={() => setShowAddUnitDialog(true)}
              color="primary"
              variant="outlined"
              size="small"
            >
              + Add New Unit
            </Button>
          </Box>
          <Typography variant="subtitle2" gutterBottom>
            Input Details
          </Typography>
          <Box
            sx={{
              overflow: "auto",
              border: "1px solid #f0f0f0",
              borderRadius: 1,
              p: 1,
            }}
          >
            {renderOutputs()}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} variant="contained">
            Cancel
          </Button>
          <Button
            onClick={handleSaveAll}
            variant="contained"
            color="primary"
            disabled={outputs.some(
              (output) => !output.outputName.trim() || (output.isConnected === "yes" && !output.connectedOutput)
            )}
          >
            Save All
          </Button>
        </DialogActions>
      </Dialog>
      <AddUnitDialog
        open={showAddUnitDialog}
        onClose={() => setShowAddUnitDialog(false)}
        onSave={handleAddUnitFromDialog}
        existingUnits={availableUnits}
      />
    </>
  );
};

export default AddNewInputFormulaDialog;